/**
 * useMorningBriefing — Her sabah 07:00'de aksiyon bülteni gönderir.
 * Client-side fallback: Edge Function (morning-briefing) çalışmazsa
 * veya offline olursa bu hook devreye girer.
 */

import { useEffect, useMemo, useRef } from 'react';
import { generateMorningBriefing, formatMorningBriefingForTelegram } from '../services/morningBriefing';
import { evaluateAllBagRisks } from '../services/contaminationRisk';
import { sendTelegramMessage } from '../services/notificationService';
import { useHarvestPredictionsV2 } from './useHarvestPredictionV2';
import type { GrowBag, AlertEvent, SensorReading, DeviceStatus, AlertNotificationSettings } from '../types';

const LS_KEY = 'gmc_morning_briefing_sent';
const TRIGGER_HOUR = 7;
const TRIGGER_MINUTE = 0;
const CHECK_INTERVAL_MS = 60_000;

interface UseMorningBriefingOpts {
  bags: GrowBag[];
  alerts: AlertEvent[];
  todayReadings: SensorReading[];
  current: SensorReading | null;
  devices: DeviceStatus[];
  healthScore: number;
  notifSettings: AlertNotificationSettings;
}

export function useMorningBriefing({
  bags,
  alerts,
  todayReadings,
  current,
  devices,
  healthScore,
  notifSettings,
}: UseMorningBriefingOpts): void {
  const harvestPredictions = useHarvestPredictionsV2();

  // Memoize expensive computation — only recalculates when inputs change, not every interval tick
  const contaminationRisks = useMemo(
    () => evaluateAllBagRisks(bags, current, todayReadings),
    [bags, current, todayReadings]
  );

  // Ref keeps latest values without recreating the interval on every data change
  const latestRef = useRef({ bags, alerts, todayReadings, devices, healthScore, notifSettings, harvestPredictions, contaminationRisks });
  useEffect(() => {
    latestRef.current = { bags, alerts, todayReadings, devices, healthScore, notifSettings, harvestPredictions, contaminationRisks };
  });

  useEffect(() => {
    const check = async () => {
      const { bags: b, alerts: a, todayReadings: tr, devices: d, healthScore: hs, notifSettings: ns, harvestPredictions: hp, contaminationRisks: cr } = latestRef.current;
      if (!ns.alertTypes.dailyReport) return;
      if (!ns.telegram?.enabled) return;

      const now = new Date();
      if (now.getHours() !== TRIGGER_HOUR || now.getMinutes() !== TRIGGER_MINUTE) return;

      const today = now.toISOString().slice(0, 10);
      if (localStorage.getItem(LS_KEY) === today) return;

      const briefingData = generateMorningBriefing({
        bags: b,
        yesterdayReadings: tr,
        alerts: a,
        devices: d,
        healthScore: hs,
        harvestPredictions: hp,
        contaminationRisks: cr,
      });

      const message = formatMorningBriefingForTelegram(briefingData);

      localStorage.setItem(LS_KEY, today);
      const sent = await sendTelegramMessage(message);
      if (!sent) {
        localStorage.removeItem(LS_KEY);
      }
    };

    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []); // Stable interval — latest values read from ref
}
